import {
  CadendarConsts,
  PatientFlags,
  RuleSchema,
  Transforms,
  Condition,
  Rule,
  RdvTypeValues,
  RdvTypeEnums,
} from "cadendar-shared";
import { useEffect, useReducer } from "react";
import _ from "underts";
import Select2 from "../../components/Select2";
import IconButton from "../main/components/IconButton";
import { FaMinus, FaPlus, FaArrowUp, FaArrowDown } from "react-icons/fa";
import IntegerInput from "../../components/IntegerInput";
import HMInput from "../../components/HMInput";
import { formaters } from "vincent-utils";
import { trpc } from "../main/components/trpc.ts";
import { RulesRecord } from "./RulesMainComp";
import Select4 from "../../components/Select4";

type RuleState = null | Rule;

interface ResetAction {
  type: "reset";
}

interface ChooseNotMoreAction {
  type: "chooseNotMore";
}

interface ChooseNotMoreNumberAction {
  type: "chooseNotMoreNumber";
  value: number;
}

interface ChooseShortcutAction {
  type: "chooseShortcut";
}

interface AddConditionAction {
  type: "addCondition";
  condition: Condition;
}

interface RemoveLastConditionAction {
  type: "removeLastCondition";
}

interface RemoveConditionAction {
  type: "removeCondition";
  index: number;
}

interface SetConditionTypeAtIndexAction {
  type: "setConditionTypeAtIndex";
  index: number;
  conditionType: Pick<Condition, "type">["type"];
}

interface SetConditionValueAtIndexAction {
  type: "setConditionValueAtIndex";
  index: number;
  value: number | PatientFlags | string;
}

type Action =
  | ResetAction
  | ChooseNotMoreAction
  | ChooseNotMoreNumberAction
  | ChooseShortcutAction
  | AddConditionAction
  | RemoveConditionAction
  | RemoveLastConditionAction
  | SetConditionTypeAtIndexAction
  | SetConditionValueAtIndexAction;

function rootReducer(state: RuleState, action: Action): RuleState {
  switch (action.type) {
    case "reset":
      return null;
    case "chooseNotMore":
      return {
        type: "notMore",
        value: 1,
        conditionsAND: getDefaultConditions(),
      };
    case "chooseNotMoreNumber":
      return {
        type: "notMore",
        value: action.value,
        conditionsAND: state?.conditionsAND || [],
      };
    case "chooseShortcut":
      return { type: "shortcut", conditionsAND: [] };
    case "addCondition":
    case "removeCondition":
      if (!state || (state.type !== "shortcut" && state.type !== "notMore")) {
        throw new Error("Invalid state");
      }
      return modifyConditionsAND(state, action);
    case "setConditionTypeAtIndex":
      if (!state || (state.type !== "shortcut" && state.type !== "notMore")) {
        throw new Error("Invalid state");
      }
      return {
        ...state,
        conditionsAND: state.conditionsAND.map((condition, index) =>
          index === action.index
            ? getDefaultConditionForType(action.conditionType)
            : condition
        ),
      };
    case "removeLastCondition":
      if (!state || (state.type !== "shortcut" && state.type !== "notMore")) {
        throw new Error("Invalid state");
      }
      return {
        ...state,
        conditionsAND: state.conditionsAND.slice(0, -1),
      };
    case "setConditionValueAtIndex":
      if (!state || (state.type !== "shortcut" && state.type !== "notMore")) {
        throw new Error("Invalid state");
      }

      return {
        ...state,
        conditionsAND: state.conditionsAND.map((condition, index) =>
          index === action.index &&
          (condition.type === "minusAge" ||
            condition.type === "plusAge" ||
            condition.type === "flags" ||
            condition.type === "rdvType" ||
            condition.type === "afterHour" ||
            condition.type === "beforeHour")
            ? updateConditionValue(condition, action.value)
            : condition
        ),
      };
  }
}

function updateConditionValue<T extends Condition>(
  condition: T,
  value: number | PatientFlags | string
): T {
  console.log("updateConditionValue", value);
  if (
    (condition.type === "minusAge" ||
      condition.type === "plusAge" ||
      condition.type === "afterHour" ||
      condition.type === "beforeHour") &&
    typeof value === "number"
  ) {
    return { ...condition, value } as T;
  } else if (condition.type === "flags" && typeof value === "string") {
    return { ...condition, value: value as PatientFlags } as T;
  } else if (condition.type === "rdvType" && typeof value === "string") {
    return { ...condition, value: value } as T;
  }
  return condition;
}

function getDefaultConditions() {
  return [{ type: "newPatient" as const }];
}

function modifyConditionsAND(
  state: Exclude<RuleState, null>,
  action: AddConditionAction | RemoveConditionAction
) {
  switch (action.type) {
    case "addCondition":
      return {
        ...state,
        conditionsAND: [...state.conditionsAND, action.condition],
      };
    case "removeCondition":
      return {
        ...state,
        conditionsAND: state.conditionsAND.filter(
          (_, index) => index !== action.index
        ),
      };
  }
}

function getDefaultConditionForType(
  conditionType: Pick<Condition, "type">["type"]
) {
  switch (conditionType) {
    case "minusAge":
      return { type: "minusAge" as const, value: 80 };
    case "plusAge":
      return { type: "plusAge" as const, value: 80 };
    case "flags":
      return { type: "flags" as const, value: "vip" as const };
    case "rdvType":
      return { type: "rdvType" as const, value: RdvTypeEnums.PCONSULT };
    case "notReferred":
      return { type: "notReferred" as const };
    case "referred":
      return { type: "referred" as const };
    case "existingPatient":
      return { type: "existingPatient" as const };
    case "newPatient":
      return { type: "newPatient" as const };
    case "afterHour":
      return { type: "afterHour" as const, value: 1080 };
    case "beforeHour":
      return { type: "beforeHour" as const, value: 120 };
    default:
      throw new Error("Invalid condition");
  }
}

const FlagSelect = (props: {
  value: PatientFlags;
  onChange: (value: PatientFlags) => void;
}) => {
  return (
    <Select2
      selected={props.value}
      onSelect={props.onChange}
      defaultKey="vip"
      keyMap={CadendarConsts.patientFlags
        .map((flag) => ({
          [flag]: flag,
        }))
        .reduce((acc, curr) => ({ ...acc, ...curr }), {})}
      className=""
    />
  );
};

const RdvTypeSelect = (props: {
  value: RdvTypeValues;
  onChange: (value: RdvTypeValues) => void;
}) => {
  const handleChange = (key: typeof RdvTypeEnums.keyType) => {
    props.onChange(RdvTypeEnums[key]);
  };
  return (
    <Select4
      selected={RdvTypeEnums.key(props.value)!}
      onSelect={handleChange}
      defaultKey={"PCONSULT"}
      className=""
      items={RdvTypeEnums.items}
    />
  );
};

type ValueSelectorDisplayerProps =
  | {
      type: "none";
      value: undefined;
      onSelect: undefined;
    }
  | {
      type: "flags";
      value: PatientFlags;
      onSelect: (flag: PatientFlags) => void;
    }
  | {
      type: "age";
      value: number;
      onSelect: (age: number) => void;
    };

interface NumberSelectProps {
  value: number;
  onChange: (value: number) => void;
}

const isNumberSelectValue = (
  value: string
): value is "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" => {
  return (
    value === "1" ||
    value === "2" ||
    value === "3" ||
    value === "4" ||
    value === "5" ||
    value === "6" ||
    value === "7" ||
    value === "8" ||
    value === "9" ||
    value === "10"
  );
};
const NumberSelect = (props: NumberSelectProps) => {
  const valueString = props.value.toString();
  if (!isNumberSelectValue(valueString)) {
    throw new Error("Invalid value");
  }
  return (
    <Select2
      selected={valueString}
      onSelect={(
        value: "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10"
      ) => props.onChange(parseInt(value))}
      defaultKey="1"
      keyMap={{
        "1": "d'un",
        "2": "de deux",
        "3": "de trois",
        "4": "de quatre",
        "5": "de cinq",
        "6": "de six",
        "7": "de sept",
        "8": "de huit",
        "9": "de neuf",
        "10": "de dix",
      }}
      className=""
    />
  );
};

interface FirstSelectProps {
  value: undefined | "notMore" | "none" | "shortcut";
  onChange: (value: undefined | "notMore" | "none" | "shortcut") => void;
}

const FirstSelect = (props: FirstSelectProps) => {
  return (
    <Select2
      selected={props.value}
      defaultString="Choisir"
      onSelect={props.onChange}
      keyMap={{
        notMore: "Pas plus",
        none: "Aucun",
        shortcut: "Les règles suivantes ne s'appliquent pas aux",
      }}
      className=""
    />
  );
};

interface PatientConditionChooserProps {
  value: Condition["type"];
  onChange: (value: Condition["type"]) => void;
}

const PatientConditionChooser = (props: PatientConditionChooserProps) => {
  return (
    <Select2
      selected={props.value}
      onSelect={props.onChange}
      defaultKey="flags"
      keyMap={{
        minusAge: "patient âgé de moins de",
        plusAge: "patient âgé de plus de",
        flags: "patient avec le drapeau",
        newPatient: "patient nouveau",
        existingPatient: "patient ancien",
        notReferred: "patient non adressé",
        referred: "patient adressé",
        rdvType: "rdv de type",
        afterHour: "rdv après",
        beforeHour: "rdv avant",
      }}
      className=""
    />
  );
};

interface WeekRuleLineProps {
  state: RuleState;
  dispatch: (action: Action) => void;
}

const WeekRuleLine = (props: WeekRuleLineProps) => {
  const state = props.state;
  const dispatch = props.dispatch;
  const horaires = trpc.horaire.byDate.useQuery({
    date: new Date(2021, 0, 1),
  }).data;
  const horairesExtremes = (horaires &&
    Transforms.horaires.horairesExtremesForWeek(horaires)) || {
    start: { hour: 0, minute: 0 },
    end: { hour: 23, minute: 59 },
  };
  const prefs = trpc.preference.get.useQuery().data;
  const hmPossibles =
    (prefs?.duree_min_rdv &&
      Transforms.horaires.getAllHMinForPlage(
        horairesExtremes,
        prefs?.duree_min_rdv
      )) ||
    [];
  const firstValue =
    state === null
      ? undefined
      : state.type === "notMore" && state.value > 0
        ? "notMore"
        : state.type === "notMore" && state.value === 0
          ? "none"
          : "shortcut";
  const handleFirstChange = (
    value: undefined | "notMore" | "none" | "shortcut"
  ) => {
    switch (value) {
      case "notMore":
        dispatch({ type: "chooseNotMore" });
        break;
      case "none":
        dispatch({ type: "chooseNotMoreNumber", value: 0 });
        break;
      case "shortcut":
        dispatch({ type: "chooseShortcut" });
        break;
      default:
        dispatch({ type: "reset" });
    }
  };
  const handleSetNumber = (value: number) => {
    dispatch({ type: "chooseNotMoreNumber", value });
  };
  return (
    <div className="p-2 grid grid-cols-[auto_auto_1fr_auto_auto] gap-3 items-center">
      <FirstSelect value={firstValue} onChange={handleFirstChange} />
      {state !== null && state.type === "notMore" && state.value > 0 && (
        <NumberSelect value={state.value} onChange={handleSetNumber} />
      )}
      {state === null || (state.type === "notMore" && state.value === 0) ? (
        <div></div> /* Placeholder pour maintenir la grille */
      ) : null}

      {state !== null &&
      (state.type === "notMore" || state.type === "shortcut") ? (
        <div className="flex flex-wrap gap-2">
          {state.conditionsAND.map((condition, index) => (
            <div key={index} className="flex gap-2 items-center">
              {index > 0 ? <span className="font-bold">et</span> : null}
              <PatientConditionChooser
                value={condition.type}
                onChange={(value) =>
                  dispatch({
                    type: "setConditionTypeAtIndex",
                    index,
                    conditionType: value,
                  })
                }
              />
              {condition.type === "minusAge" || condition.type === "plusAge" ? (
                <div className="flex gap-2 items-center w-20">
                  <IntegerInput
                    value={condition.value}
                    showValidation={false}
                    onValueChange={(value) => {
                      if (value) {
                        dispatch({
                          type: "setConditionValueAtIndex",
                          index,
                          value: value,
                        });
                      }
                    }}
                  />{" "}
                  ans
                </div>
              ) : null}
              {condition.type === "flags" ? (
                <FlagSelect
                  value={condition.value}
                  onChange={(value) => {
                    dispatch({
                      type: "setConditionValueAtIndex",
                      index,
                      value,
                    });
                  }}
                />
              ) : null}
              {condition.type === "rdvType" ? (
                <RdvTypeSelect
                  value={condition.value}
                  onChange={(value) => {
                    dispatch({
                      type: "setConditionValueAtIndex",
                      index,
                      value,
                    });
                  }}
                />
              ) : null}
              {condition.type === "afterHour" ? (
                <HMInput
                  hmSelected={
                    formaters.getHourMinutesFromMinutes(condition.value) || {
                      hour: 12,
                      minute: 0,
                    }
                  }
                  handleHMChange={(hm: { hour: number; minute: number }) =>
                    dispatch({
                      type: "setConditionValueAtIndex",
                      value: formaters.getHourAsMinutesFromHMObject(hm),
                      index,
                    })
                  }
                  hmPossibles={hmPossibles}
                />
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div></div> /* Placeholder pour maintenir la grille */
      )}

      {state !== null &&
      (state.type === "notMore" || state.type === "shortcut") ? (
        <IconButton
          tooltip="Supprimer dernière condition"
          onClick={() => dispatch({ type: "removeLastCondition" })}
        >
          <FaMinus />
        </IconButton>
      ) : (
        <div></div> /* Placeholder pour maintenir la grille */
      )}

      {state !== null &&
      (state.type === "notMore" || state.type === "shortcut") ? (
        <IconButton
          tooltip="Ajouter une condition"
          onClick={() =>
            dispatch({
              type: "addCondition",
              condition: { type: "newPatient" },
            })
          }
        >
          <FaPlus />
        </IconButton>
      ) : (
        <div></div> /* Placeholder pour maintenir la grille */
      )}
    </div>
  );
};

const WeekRuleReducer = (
  state: RuleState[] = [],
  action:
    | { ruleIndex: number; action: Action; type: "lineAction" }
    | { type: "addLine" }
    | { type: "removeLine"; lineNumber: number }
    | { type: "moveLineUp"; lineNumber: number }
    | { type: "moveLineDown"; lineNumber: number }
    | { type: "assignState"; state: RuleState[] }
) => {
  if (action.type === "addLine") {
    return [...state, null];
  }
  if (action.type === "removeLine") {
    return [
      ...state.slice(0, action.lineNumber),
      ...state.slice(action.lineNumber + 1),
    ];
  }
  if (action.type === "moveLineUp") {
    if (action.lineNumber === 0) return state; // Déjà en haut
    const newState = [...state];
    [newState[action.lineNumber - 1], newState[action.lineNumber]] = [
      newState[action.lineNumber],
      newState[action.lineNumber - 1],
    ];
    return newState;
  }
  if (action.type === "moveLineDown") {
    if (action.lineNumber === state.length - 1) return state; // Déjà en bas
    const newState = [...state];
    [newState[action.lineNumber], newState[action.lineNumber + 1]] = [
      newState[action.lineNumber + 1],
      newState[action.lineNumber],
    ];
    return newState;
  }
  if (action.type === "lineAction") {
    return state.map((r, idx) =>
      idx === action.ruleIndex ? rootReducer(r, action.action) : r
    );
  }
  if (action.type === "assignState") {
    return action.state;
  }
  return state;
};

interface WeekRulesCompProps {
  rules: RulesRecord | null;
}

export const WeekRulesComp = (props: WeekRulesCompProps) => {
  console.log("props.rules", props.rules);
  const [state, dispatch] = useReducer(WeekRuleReducer, [null]);
  useEffect(() => {
    if (props.rules) {
      dispatch({ type: "assignState", state: props.rules });
    }
  }, [props.rules]);
  const hasChanges = JSON.stringify(state) !== JSON.stringify(props.rules);
  const isValid = state.every((rule) => RuleSchema.safeParse(rule).success);
  const handleDispatch =
    (ruleIndex: number) =>
    (
      action: Extract<
        Parameters<typeof WeekRuleReducer>[1],
        { action: any }
      >["action"]
    ) =>
      dispatch({ ruleIndex, action, type: "lineAction" });
  console.log("state", state);

  const utils = trpc.useUtils();
  const submitRulesMutation = trpc.rule.submitWeekRulesForOwner.useMutation({
    onSuccess: () => {
      utils.rule.getAll.invalidate();
    },
  });
  const handleSubmit = () => {
    const parsed = state.map((rule) => {
      return RuleSchema.parse(rule);
    });
    submitRulesMutation.mutate(parsed);
  };
  return (
    <div className="m-4">
      <div className="text-xl">
        Quotas par <span className="font-bold">semaine</span>
      </div>
      <div className="m-1">
        {state.map((rule, idx) => (
          <div key={idx} className="flex items-center">
            <div className="flex gap-1">
              {idx > 0 ? (
                <IconButton
                  onClick={() =>
                    dispatch({ type: "moveLineUp", lineNumber: idx })
                  }
                  tooltip="Déplacer vers le haut"
                >
                  <FaArrowUp />
                </IconButton>
              ) : (
                <div className="w-8"></div>
              )}
              {idx < state.length - 1 ? (
                <IconButton
                  onClick={() =>
                    dispatch({ type: "moveLineDown", lineNumber: idx })
                  }
                  tooltip="Déplacer vers le bas"
                >
                  <FaArrowDown />
                </IconButton>
              ) : (
                <div className="w-8"></div>
              )}
            </div>
            <IconButton
              onClick={() => dispatch({ type: "removeLine", lineNumber: idx })}
              tooltip="Retirer règle"
            >
              <FaMinus />
            </IconButton>
            <WeekRuleLine state={rule} dispatch={handleDispatch(idx)} />
          </div>
        ))}
        <div className="pt-2">
          <IconButton
            onClick={() => dispatch({ type: "addLine" })}
            tooltip="Ajouter une règle"
          >
            <FaPlus />
          </IconButton>
        </div>
        <div className="pt-3">
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
            onClick={handleSubmit}
            disabled={!hasChanges || !isValid}
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

interface DayRulesCompProps {
  rules: RulesRecord | null;
}

export const DayRulesComp = (props: DayRulesCompProps) => {
  console.log("props.rules", props.rules);
  const [state, dispatch] = useReducer(WeekRuleReducer, [null]);
  useEffect(() => {
    if (props.rules) {
      dispatch({ type: "assignState", state: props.rules });
    }
  }, [props.rules]);
  const hasChanges = JSON.stringify(state) !== JSON.stringify(props.rules);
  const isValid = state.every((rule) => RuleSchema.safeParse(rule).success);
  const handleDispatch =
    (ruleIndex: number) =>
    (
      action: Extract<
        Parameters<typeof WeekRuleReducer>[1],
        { action: any }
      >["action"]
    ) =>
      dispatch({ ruleIndex, action, type: "lineAction" });
  console.log("state", state);

  const utils = trpc.useUtils();
  const submitRulesMutation = trpc.rule.submitDayRulesForOwner.useMutation({
    onSuccess: () => {
      utils.rule.getAll.invalidate();
    },
  });
  const handleSubmit = () => {
    const parsed = state.map((rule) => {
      return RuleSchema.parse(rule);
    });
    submitRulesMutation.mutate(parsed);
  };
  return (
    <div className="m-4">
      <div className="text-xl">
        Quotas par <span className="font-bold">jour</span>
      </div>
      <div className="m-1">
        {state.map((rule, idx) => (
          <div key={idx} className="flex items-center">
            <div className="flex gap-1">
              {idx > 0 ? (
                <IconButton
                  onClick={() =>
                    dispatch({ type: "moveLineUp", lineNumber: idx })
                  }
                  tooltip="Déplacer vers le haut"
                >
                  <FaArrowUp />
                </IconButton>
              ) : (
                <div className="w-8"></div>
              )}
              {idx < state.length - 1 ? (
                <IconButton
                  onClick={() =>
                    dispatch({ type: "moveLineDown", lineNumber: idx })
                  }
                  tooltip="Déplacer vers le bas"
                >
                  <FaArrowDown />
                </IconButton>
              ) : (
                <div className="w-8"></div>
              )}
            </div>
            <IconButton
              onClick={() => dispatch({ type: "removeLine", lineNumber: idx })}
              tooltip="Retirer règle"
            >
              <FaMinus />
            </IconButton>
            <WeekRuleLine state={rule} dispatch={handleDispatch(idx)} />
          </div>
        ))}
        <div className="pt-2">
          <IconButton
            onClick={() => dispatch({ type: "addLine" })}
            tooltip="Ajouter une règle"
          >
            <FaPlus />
          </IconButton>
        </div>
        <div className="pt-3">
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
            onClick={handleSubmit}
            disabled={!hasChanges || !isValid}
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};
